import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  createElement,
  useSyncExternalStore,
  type JSX,
  type PropsWithChildren,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type QueryCacheNotifyEvent,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';

type QueryKeyPart =
  | QueryKey
  | Record<string, unknown>
  | string
  | number
  | boolean
  | null
  | undefined;

type BivariantCallback<Args extends unknown[], Return> = {
  bivarianceHack(...args: Args): Return;
}['bivarianceHack'];

interface ResourceDefaults {
  staleTime?: number;
}

export interface ResourceListDefinition<ListParams, ListResult> {
  key: (params: ListParams) => QueryKeyPart;
  fetch: (params: ListParams & { signal?: AbortSignal }) => Promise<ListResult>;
  staleTime?: number;
  enabled?: (params: ListParams) => boolean;
  subscribe?: (context: {
    params: ListParams;
    queryClient: QueryClient;
  }) => (() => void) | undefined;
}

export interface ResourceDetailDefinition<DetailParams, DetailResult> {
  key: (params: DetailParams) => QueryKeyPart;
  fetch: (params: DetailParams & { signal?: AbortSignal }) => Promise<DetailResult>;
  staleTime?: number;
  enabled?: (params: DetailParams) => boolean;
  subscribe?: (context: {
    params: DetailParams;
    queryClient: QueryClient;
  }) => (() => void) | undefined;
}

export interface OptimisticUpdateContext<Variables> {
  queryClient: QueryClient;
  variables: Variables;
}

export interface OptimisticUpdateSnapshot {
  rollback?: () => void;
}

export interface MutationInvalidateContext<Result, Variables> {
  queryClient: QueryClient;
  variables: Variables;
  data?: Result;
}

export interface ResourceMutationDefinition<Variables, Result> {
  mutationFn: BivariantCallback<[Variables], Promise<Result>>;
  optimisticUpdate?: BivariantCallback<
    [OptimisticUpdateContext<Variables>],
    OptimisticUpdateSnapshot | undefined
  >;
  invalidate?: BivariantCallback<[MutationInvalidateContext<Result, Variables>], QueryKey[]>;
  onSuccess?: BivariantCallback<
    [{ data: Result; variables: Variables; queryClient: QueryClient }],
    void | Promise<void>
  >;
  onError?: BivariantCallback<[unknown, Variables], void | Promise<void>>;
}

export interface ResourceDefinition<
  ListParams,
  ListResult,
  DetailParams,
  DetailResult,
  Mutations extends Record<string, ResourceMutationDefinition<unknown, unknown>> = Record<
    string,
    ResourceMutationDefinition<unknown, unknown>
  >,
> {
  scope: string;
  defaults?: ResourceDefaults;
  list?: ResourceListDefinition<ListParams, ListResult>;
  detail?: ResourceDetailDefinition<DetailParams, DetailResult>;
  mutations?: Mutations;
}

export interface ResourceCacheHelpers<ListParams, ListResult, DetailParams, DetailResult> {
  writeListCache: (queryClient: QueryClient, params: ListParams, data: ListResult) => void;
  writeDetailCache: (queryClient: QueryClient, params: DetailParams, data: DetailResult) => void;
  invalidateList: (queryClient: QueryClient, params?: ListParams) => Promise<void>;
  invalidateDetail: (queryClient: QueryClient, params: DetailParams) => Promise<void>;
  writeMany: (queryClient: QueryClient, entries: Array<{ key: QueryKey; data: unknown }>) => void;
}

export interface ResourceKeys<ListParams, DetailParams> {
  base: () => QueryKey;
  list: (params: ListParams) => QueryKey;
  detail: (params: DetailParams) => QueryKey;
  mutation: (name: string, variables?: unknown) => QueryKey;
}

export interface ResourceHooks<
  ListParams,
  ListResult,
  DetailParams,
  DetailResult,
  Mutations extends Record<string, ResourceMutationDefinition<unknown, unknown>>,
> {
  keys: ResourceKeys<ListParams, DetailParams>;
  cache: ResourceCacheHelpers<ListParams, ListResult, DetailParams, DetailResult>;
  createListQueryOptions: (
    params: ListParams,
    overrides?: Partial<UseQueryOptions<ListResult>>,
  ) => UseQueryOptions<ListResult>;
  createDetailQueryOptions: (
    params: DetailParams,
    overrides?: Partial<UseQueryOptions<DetailResult>>,
  ) => UseQueryOptions<DetailResult>;
  useList: (
    params: ListParams,
    overrides?: Partial<UseQueryOptions<ListResult>>,
  ) => UseQueryResult<ListResult>;
  useDetail: (
    params: DetailParams,
    overrides?: Partial<UseQueryOptions<DetailResult>>,
  ) => UseQueryResult<DetailResult>;
  useRealtimeInvalidation: (
    params: ListParams | DetailParams,
    subscribe?: () => (() => void) | undefined,
  ) => void;
  useMutation: <Name extends keyof Mutations>(
    name: Name,
    options?: Partial<
      UseMutationOptions<
        Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
          ? Result
          : unknown,
        unknown,
        Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result>
          ? Vars
          : never,
        OptimisticUpdateSnapshot
      >
    >,
  ) => UseMutationResult<
    Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
      ? Result
      : unknown,
    unknown,
    Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result> ? Vars : never,
    OptimisticUpdateSnapshot
  >;
  useListSelector: <Selected>(
    params: ListParams,
    selector: (data: ListResult | undefined) => Selected,
    equals?: (a: Selected, b: Selected) => boolean,
  ) => Selected;
  useDetailSelector: <Selected>(
    params: DetailParams,
    selector: (data: DetailResult | undefined) => Selected,
    equals?: (a: Selected, b: Selected) => boolean,
  ) => Selected;
  toAsyncState: <Data>(
    result: Pick<UseQueryResult<Data>, 'data' | 'isLoading' | 'isFetching' | 'error'>,
  ) => {
    data: Data | null;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
  };
}

const DEFAULT_STALE_TIME_MS = 60 * 1000;

function normalizeKeyPart(part: QueryKeyPart): readonly unknown[] {
  if (part === undefined) {
    return [];
  }

  if (Array.isArray(part)) {
    return part;
  }

  return [part];
}

function buildKey(scope: string, segment: string, part?: QueryKeyPart): QueryKey {
  const base: unknown[] = [scope, segment];
  const extra = normalizeKeyPart(part);
  return [...base, ...extra];
}

function stableSerialize(value: unknown): string {
  const seen = new WeakSet();
  const serialize = (input: unknown): unknown => {
    if (input === null || typeof input !== 'object') {
      if (typeof input === 'function') {
        return input.name || 'function';
      }
      return input;
    }
    if (seen.has(input)) {
      return '[circular]';
    }
    seen.add(input);
    if (Array.isArray(input)) {
      return input.map(serialize);
    }
    const entries = Object.keys(input as Record<string, unknown>)
      .sort()
      .map((key) => [key, serialize((input as Record<string, unknown>)[key])]);
    return Object.fromEntries(entries);
  };
  return JSON.stringify(serialize(value));
}

function serializeKey(key: QueryKey): string {
  return stableSerialize(key);
}

function createSelectorHook({ getKey }: { getKey: (params: unknown) => QueryKey }) {
  return <Selected>(
    params: unknown,
    selector: (data: unknown) => Selected,
    equals: (a: Selected, b: Selected) => boolean = Object.is,
  ): Selected => {
    const queryClient = useQueryClient();
    const queryKey = getKey(params);
    const serializedKey = serializeKey(queryKey);

    const selectorRef = useRef<(data: unknown) => Selected>(selector);
    selectorRef.current = selector;
    const equalsRef = useRef<(a: Selected, b: Selected) => boolean>(equals);
    equalsRef.current = equals;
    const snapshotRef = useRef<Selected | undefined>(undefined);

    const getSnapshot = () => {
      const data = queryClient.getQueryData(queryKey);
      const next = selectorRef.current(data);
      if (snapshotRef.current === undefined || !equalsRef.current(snapshotRef.current, next)) {
        snapshotRef.current = next;
      }
      return snapshotRef.current;
    };

    const subscribe = (onStoreChange: () => void) => {
      const handleEvent = (event: QueryCacheNotifyEvent) => {
        const queryKeyFromEvent = event.query.queryKey as QueryKey;
        if (serializeKey(queryKeyFromEvent) === serializedKey) {
          onStoreChange();
        }
      };
      return queryClient.getQueryCache().subscribe(handleEvent);
    };

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  };
}

function toAsyncState<Data>(
  result: Pick<UseQueryResult<Data>, 'data' | 'isLoading' | 'isFetching' | 'error'>,
) {
  return {
    data: result.data ?? null,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error ?? null,
  };
}

function ensureListDefinition<ListParams, ListResult>(
  list: ResourceListDefinition<ListParams, ListResult> | undefined,
  scope: string,
): ResourceListDefinition<ListParams, ListResult> {
  if (!list) {
    throw new Error(`List definition missing for scope "${scope}".`);
  }
  return list;
}

function ensureDetailDefinition<DetailParams, DetailResult>(
  detail: ResourceDetailDefinition<DetailParams, DetailResult> | undefined,
  scope: string,
): ResourceDetailDefinition<DetailParams, DetailResult> {
  if (!detail) {
    throw new Error(`Detail definition missing for scope "${scope}".`);
  }
  return detail;
}

export function createResourceHooks<
  ListParams,
  ListResult,
  DetailParams,
  DetailResult,
  Mutations extends Record<string, ResourceMutationDefinition<unknown, unknown>>,
>(
  definition: ResourceDefinition<ListParams, ListResult, DetailParams, DetailResult, Mutations>,
): ResourceHooks<ListParams, ListResult, DetailParams, DetailResult, Mutations> {
  const { scope, list, detail, defaults, mutations } = definition;
  const listDefinition = list;
  const detailDefinition = detail;
  const mutationDefinition = mutations ?? ({} as Mutations);
  const fallbackStaleTime = defaults?.staleTime ?? DEFAULT_STALE_TIME_MS;

  const keys: ResourceKeys<ListParams, DetailParams> = {
    base: () => [scope],
    list: (params: ListParams) =>
      buildKey(scope, 'list', ensureListDefinition(listDefinition, scope).key(params)),
    detail: (params: DetailParams) =>
      buildKey(scope, 'detail', ensureDetailDefinition(detailDefinition, scope).key(params)),
    mutation: (name: string, variables?: unknown) => buildKey(scope, 'mutation', [name, variables]),
  };

  const cache: ResourceCacheHelpers<ListParams, ListResult, DetailParams, DetailResult> = {
    writeListCache: (queryClient, params, data) => {
      queryClient.setQueryData(keys.list(params), data);
    },
    writeDetailCache: (queryClient, params, data) => {
      queryClient.setQueryData(keys.detail(params), data);
    },
    invalidateList: async (queryClient, params) => {
      if (listDefinition === undefined) {
        return;
      }
      const queryKey = params !== undefined ? keys.list(params) : [scope, 'list'];
      await queryClient.invalidateQueries({ queryKey });
    },
    invalidateDetail: async (queryClient, params) => {
      if (detailDefinition === undefined) {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: keys.detail(params) });
    },
    writeMany: (queryClient, entries) => {
      entries.forEach(({ key, data }) => {
        queryClient.setQueryData(key, data);
      });
    },
  };

  const useListSelector = createSelectorHook({
    getKey: (params: unknown) => keys.list(params as ListParams),
  }) as <Selected>(
    params: ListParams,
    selector: (data: ListResult | undefined) => Selected,
    equals?: (a: Selected, b: Selected) => boolean,
  ) => Selected;

  const useDetailSelector = createSelectorHook({
    getKey: (params: unknown) => keys.detail(params as DetailParams),
  }) as <Selected>(
    params: DetailParams,
    selector: (data: DetailResult | undefined) => Selected,
    equals?: (a: Selected, b: Selected) => boolean,
  ) => Selected;

  const createListQueryOptions = (
    params: ListParams,
    overrides: Partial<UseQueryOptions<ListResult>> = {},
  ): UseQueryOptions<ListResult> => {
    const safeList = ensureListDefinition(listDefinition, scope);
    const enabled = typeof safeList.enabled === 'function' ? safeList.enabled(params) : true;

    return {
      queryKey: keys.list(params),
      queryFn: async ({ signal }) => {
        return await safeList.fetch({ ...params, signal });
      },
      staleTime: safeList.staleTime ?? fallbackStaleTime,
      enabled,
      ...overrides,
    };
  };

  const createDetailQueryOptions = (
    params: DetailParams,
    overrides: Partial<UseQueryOptions<DetailResult>> = {},
  ): UseQueryOptions<DetailResult> => {
    const safeDetail = ensureDetailDefinition(detailDefinition, scope);
    const enabled = typeof safeDetail.enabled === 'function' ? safeDetail.enabled(params) : true;

    return {
      queryKey: keys.detail(params),
      queryFn: async ({ signal }) => {
        return await safeDetail.fetch({ ...params, signal });
      },
      staleTime: safeDetail.staleTime ?? fallbackStaleTime,
      enabled,
      ...overrides,
    };
  };

  const useList = (
    params: ListParams,
    overrides: Partial<UseQueryOptions<ListResult>> = {},
  ): UseQueryResult<ListResult> => {
    const options = createListQueryOptions(params, overrides);
    const queryClient = useQueryClient();
    const paramHash = useMemo(() => serializeKey(params as QueryKey), [params]);
    const subscribe = listDefinition?.subscribe;
    useEffect(() => {
      if (!subscribe) {
        return;
      }
      const teardown = subscribe({ params, queryClient });
      return typeof teardown === 'function' ? teardown : undefined;
    }, [paramHash, params, queryClient, subscribe]);
    return useQuery(options);
  };

  const useDetail = (
    params: DetailParams,
    overrides: Partial<UseQueryOptions<DetailResult>> = {},
  ): UseQueryResult<DetailResult> => {
    const options = createDetailQueryOptions(params, overrides);
    const queryClient = useQueryClient();
    const paramHash = useMemo(() => serializeKey(params as QueryKey), [params]);
    const subscribe = detailDefinition?.subscribe;
    useEffect(() => {
      if (!subscribe) {
        return;
      }
      const teardown = subscribe({ params, queryClient });
      return typeof teardown === 'function' ? teardown : undefined;
    }, [paramHash, params, queryClient, subscribe]);
    return useQuery(options);
  };

  const useMutationHook = <Name extends keyof Mutations>(
    name: Name,
    options: Partial<
      UseMutationOptions<
        Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
          ? Result
          : unknown,
        unknown,
        Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result>
          ? Vars
          : never,
        OptimisticUpdateSnapshot
      >
    > = {},
  ): UseMutationResult<
    Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
      ? Result
      : unknown,
    unknown,
    Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result> ? Vars : never,
    OptimisticUpdateSnapshot
  > => {
    const definitionForName = mutationDefinition[name] as Mutations[Name] | undefined;

    if (definitionForName === undefined) {
      throw new Error(`Mutation "${String(name)}" is not defined for scope "${scope}".`);
    }

    const queryClient = useQueryClient();
    const {
      onSuccess: userOnSuccess,
      onError: userOnError,
      onMutate: userOnMutate,
      ...restOptions
    } = options;

    return useMutation({
      mutationKey: keys.mutation(String(name)),
      mutationFn: definitionForName.mutationFn as (
        variables: Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result>
          ? Vars
          : never,
      ) => Promise<
        Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
          ? Result
          : unknown
      >,
      onMutate: async (variables) => {
        const snapshot = definitionForName.optimisticUpdate
          ? definitionForName.optimisticUpdate({
              queryClient,
              variables,
            } as OptimisticUpdateContext<
              Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result>
                ? Vars
                : never
            >)
          : {};

        const userContext = userOnMutate
          ? await userOnMutate(variables, undefined as never)
          : undefined;

        return {
          ...(snapshot ?? {}),
          userContext,
        };
      },
      onSuccess: async (data, variables, context, mutation) => {
        const invalidationKeys =
          definitionForName.invalidate?.({
            queryClient,
            variables,
            data,
          } as MutationInvalidateContext<
            Mutations[Name] extends ResourceMutationDefinition<infer _Vars, infer Result>
              ? Result
              : never,
            Mutations[Name] extends ResourceMutationDefinition<infer Vars, infer _Result>
              ? Vars
              : never
          >) ?? [];

        await Promise.all(
          invalidationKeys.map(async (queryKey) => {
            await queryClient.invalidateQueries({ queryKey });
          }),
        );

        if (definitionForName.onSuccess) {
          await definitionForName.onSuccess({
            data: data as Mutations[Name] extends ResourceMutationDefinition<
              infer _Vars,
              infer Result
            >
              ? Result
              : never,
            variables: variables as Mutations[Name] extends ResourceMutationDefinition<
              infer Vars,
              infer _Result
            >
              ? Vars
              : never,
            queryClient,
          });
        }

        if (userOnSuccess) {
          await userOnSuccess(data, variables, context, mutation);
        }
      },
      onError: async (error, variables, context, mutation) => {
        if (context?.rollback) {
          context.rollback();
        }

        if (definitionForName.onError) {
          await definitionForName.onError(
            error,
            variables as Mutations[Name] extends ResourceMutationDefinition<
              infer Vars,
              infer _Result
            >
              ? Vars
              : never,
          );
        }

        if (userOnError) {
          await userOnError(error, variables, context, mutation);
        }
      },
      ...restOptions,
    });
  };

  const useRealtimeInvalidation = (
    params: ListParams | DetailParams,
    subscribe?: () => (() => void) | undefined,
  ): void => {
    useEffect(() => {
      if (!subscribe) {
        return;
      }
      const teardown = subscribe();
      return () => {
        if (typeof teardown === 'function') {
          teardown();
        }
      };
    }, [params, subscribe]);
  };

  return {
    keys,
    cache,
    createListQueryOptions,
    createDetailQueryOptions,
    useList,
    useDetail,
    useMutation: useMutationHook,
    useRealtimeInvalidation,
    useListSelector,
    useDetailSelector,
    toAsyncState,
  };
}

interface ResourceSelectorContextValue<
  ListParams,
  ListResult,
  DetailParams,
  DetailResult,
  Mutations extends Record<string, ResourceMutationDefinition<unknown, unknown>>,
> {
  resource: ResourceHooks<ListParams, ListResult, DetailParams, DetailResult, Mutations>;
}

/**
 * Optional selector-friendly context wrapper for resource hooks.
 */
export function createResourceSelectorContext<
  ListParams,
  ListResult,
  DetailParams,
  DetailResult,
  Mutations extends Record<string, ResourceMutationDefinition<unknown, unknown>>,
>(resource: ResourceHooks<ListParams, ListResult, DetailParams, DetailResult, Mutations>) {
  const Context = createContext<ResourceSelectorContextValue<
    ListParams,
    ListResult,
    DetailParams,
    DetailResult,
    Mutations
  > | null>(null);

  function Provider({ children }: PropsWithChildren): JSX.Element {
    const value = useMemo(() => ({ resource }), []);
    return createElement(Context.Provider, { value }, children);
  }

  function useResource() {
    const context = useContext(Context);
    if (context === null) {
      throw new Error('useResource must be used within a ResourceSelectorContext provider');
    }
    return context.resource;
  }

  return {
    Provider,
    useResource,
    useListSelector: resource.useListSelector,
    useDetailSelector: resource.useDetailSelector,
    toAsyncState: resource.toAsyncState,
  };
}

interface SupabaseSubscribeOptions<Params> {
  channel: string;
  table: string;
  event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  filter?: string;
  mapQueryKeys: (params: Params) => QueryKey[];
}

/**
 * Helper to wire Supabase channel subscriptions into list/detail subscribe hooks.
 */
export function createSupabaseChannelSubscribe<Params>({
  supabase,
  channel,
  table,
  event = '*',
  filter,
  mapQueryKeys,
}: SupabaseSubscribeOptions<Params> & { supabase: SupabaseClient }) {
  return ({ params, queryClient }: { params: Params; queryClient: QueryClient }) => {
    const subscription = supabase
      .channel(channel)
      .on(
        'postgres_changes' as const,
        {
          event,
          schema: 'public',
          table,
          filter,
        } as never,
        () => {
          const keys = mapQueryKeys(params);
          keys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key }).catch(() => undefined);
          });
        },
      )
      .subscribe();

    return () => {
      void subscription.unsubscribe();
    };
  };
}
